import React, { useEffect, useRef, useState } from 'react';
import DocPreviewSheet from './DocPreviewSheet';
import { logUxEvent } from '../telemetry/uxLogger';

/**
 * Prévia do documento antes do pagamento: o paciente vê a folha nítida por um
 * instante e ela então desfoca, com o botão de pagamento no centro.
 *
 * A janela nítida é concedida uma única vez por documento (`revealKey`). Sem
 * isso, bastava fechar e reabrir a tela para ter quantas janelas de print o
 * paciente quisesse.
 *
 * O blur é CSS e serve como gatilho de conversão, não como controle de acesso:
 * o conteúdo está no DOM durante toda a exibição. O que o pagamento libera é o
 * documento oficial — assinado (ICP-Brasil), com token e QR Code válidos. Esta
 * folha não tem nada disso e traz a observação de exemplo sem validade legal.
 */

export const REVEAL_MS = 1000;

const SEEN_PREFIX = 'cm_doc_preview_seen:';

function markSeen(key) {
  if (!key) return false;
  try {
    const k = SEEN_PREFIX + key;
    if (window.localStorage.getItem(k)) return true;
    window.localStorage.setItem(k, String(Date.now()));
    return false;
  } catch {
    // Navegação privada ou storage cheio: sem memória, cai no comportamento
    // antigo (uma janela nítida por abertura). Preferível a não mostrar nada.
    return false;
  }
}

export default function DocPreviewReveal({
  doc,
  priceLabel = 'R$ 79,90',
  onUnlock,
  unlockHref = null,
  hideUnlock = false,
  revealKey = null,
  revealMs = REVEAL_MS,
}) {
  // A marcação acontece no primeiro render, não ao fim do timer: fechar a tela
  // no meio da janela nítida não pode devolver uma janela nova.
  const [locked, setLocked] = useState(() => markSeen(revealKey));
  const shownRef = useRef(false);

  useEffect(() => {
    if (locked) return undefined;
    const t = setTimeout(() => setLocked(true), revealMs);
    return () => clearTimeout(t);
    // `locked` só entra aqui para não reagendar o timer depois de desfocar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealMs]);

  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    try {
      logUxEvent('conduct_preview_locked_shown', {
        doc_type: doc?.docType || 'atestado',
        reveal_ms: revealMs,
        // Reabertura: a folha já nasce desfocada. Separa, na análise, quem viu
        // o documento de quem só viu o borrão.
        already_seen: locked,
      });
    } catch {}
    // Só o primeiro render interessa; `locked` muda ao fim da janela nítida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, revealMs]);

  const handleUnlock = () => {
    try {
      logUxEvent('conduct_preview_unlock_click', {
        origin: 'document_center',
        doc_type: doc?.docType || 'atestado',
      });
    } catch {}
    if (typeof onUnlock === 'function') onUnlock();
  };

  const blockCopy = (e) => {
    e.preventDefault();
    return false;
  };

  const cta = hideUnlock ? (
    <span
      style={{
        fontSize: '0.72rem',
        fontWeight: 600,
        color: '#475569',
        background: 'rgba(255,255,255,0.94)',
        padding: '0.4rem 0.7rem',
        borderRadius: 999,
        textAlign: 'center',
      }}
    >
      O paciente vê aqui o botão para liberar o documento oficial
    </span>
  ) : (
    <>
      {unlockHref ? (
        <a
          className="doc-preview-cta"
          href={unlockHref}
          onClick={handleUnlock}
          style={ctaStyle}
        >
          <span aria-hidden="true">🔓</span>
          Liberar documento oficial — {priceLabel}
        </a>
      ) : (
        <button type="button" className="doc-preview-cta" onClick={handleUnlock} style={ctaStyle}>
          <span aria-hidden="true">🔓</span>
          Liberar documento oficial — {priceLabel}
        </button>
      )}
      <span
        style={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: '#0F172A',
          background: 'rgba(255,255,255,0.92)',
          padding: '0.25rem 0.6rem',
          borderRadius: 999,
        }}
      >
        PIX aprovado na hora · pagamento único
      </span>
    </>
  );

  return (
    <div
      data-cm-preview={locked ? 'locked' : 'clear'}
      style={{
        position: 'relative',
        borderRadius: 12,
        border: '1px solid #E2E8F0',
        background: '#fff',
        overflow: 'hidden',
        userSelect: 'none',
      }}
      onContextMenu={blockCopy}
      onCopy={blockCopy}
    >
      <style>{`
        @keyframes docPreviewPulse {
          0%,100% { box-shadow: 0 12px 28px rgba(211,47,47,0.32); }
          50%     { box-shadow: 0 12px 38px rgba(211,47,47,0.48); }
        }
        .doc-preview-cta:hover { transform: translateY(-1px); }
        .doc-preview-cta:active { transform: translateY(1px); }
        @media (prefers-reduced-motion: reduce) {
          .doc-preview-cta { animation: none !important; }
        }
      `}</style>

      <div
        aria-hidden={locked ? 'true' : undefined}
        style={{
          filter: locked ? 'blur(7px)' : 'none',
          transform: locked ? 'scale(1.02)' : 'none',
          transition: 'filter 700ms ease, transform 700ms ease',
        }}
      >
        <DocPreviewSheet {...doc} />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0 1rem',
          background: locked ? 'rgba(248,250,252,0.35)' : 'transparent',
          opacity: locked ? 1 : 0,
          pointerEvents: locked ? 'auto' : 'none',
          transition: 'opacity 500ms ease',
        }}
      >
        {cta}
      </div>
    </div>
  );
}

const ctaStyle = {
  border: 'none',
  borderRadius: 999,
  padding: '0.85rem 1.5rem',
  background: '#D32F2F',
  color: '#fff',
  fontWeight: 800,
  fontSize: '0.95rem',
  cursor: 'pointer',
  textDecoration: 'none',
  animation: 'docPreviewPulse 2.4s ease-in-out infinite',
  transition: 'transform 0.12s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
  maxWidth: '100%',
  textAlign: 'center',
};
